import { Mask, Icon, Text, Button, Alert, List, Flex, Link } from '@smarterservices/smarter-ui';
import { useEffect, useState, useRef } from 'react';
import CONSTANTS from '../../../config/constants';
import { getStore, useExamSessionStore, useAppStore } from '../../../stores/StoreContext';
import LabelAndText from '../../Common/LabelAndText';

const BaseOverlay = ({
    lastConnectionChange,
    size = 'large',
    iconSize = 'large',
    titleVariant = 'h2',
    padding = 'large',
    showExpandedInfo = true,
    zIndex = 5000,
    ttlConstant,
    onDataExtract,
    onAcknowledgement,
    children
}) => {
    const EventStore = getStore('eventStore');
    const ExamSession = getStore('examSessionStore');
    const [overlayData, setOverlayData] = useState({
        title: null,
        subtitle: null,
        buttonText: null,
        note: null,
        iconName: null,
        iconColor: 'success',
        alertTitle: null,
        alertVariant: 'info'
    });
    const [isExpired, setIsExpired] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(100);
    const timeoutRef = useRef(null);
    const intervalRef = useRef(null);

    const attachedToProctorRoomSid = useAppStore((state) => state.attachedToProctorRoomSid);
    const courseData = useExamSessionStore((state) => state.courseData);
    const sessionData = useExamSessionStore((state) => state.sessionData);

    const handleAcknowledgement = async () => {
        const currentChange = ExamSession.lastConnectionChange;
        ExamSession.removeOverlay();
        EventStore.trigger('onLastConnectionChangeAcknowledgement', { change: currentChange });

        if (onAcknowledgement) {
            await onAcknowledgement(currentChange);
        }
    };

    useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        if (lastConnectionChange?.metadata && ttlConstant) {
            const expirationTime = lastConnectionChange.changedAt + ttlConstant;
            const currentTime = Date.now();

            if (expirationTime <= currentTime) {
                setIsExpired(true);
                handleAcknowledgement();
                return;
            }

            setIsExpired(false);

            if (onDataExtract) {
                const extractedData = onDataExtract(lastConnectionChange, attachedToProctorRoomSid);
                setOverlayData(extractedData);
            }

            const remainingTime = expirationTime - currentTime;

            // Set up progress bar animation
            setTimeRemaining(100);
            const startTime = Date.now();

            intervalRef.current = setInterval(() => {
                const elapsed = Date.now() - startTime;
                const percentage = Math.max(0, 100 - (elapsed / remainingTime) * 100);
                setTimeRemaining(percentage);

                if (percentage <= 0) {
                    clearInterval(intervalRef.current);
                }
            }, 100);

            timeoutRef.current = setTimeout(() => {
                handleAcknowledgement();
            }, remainingTime);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [lastConnectionChange]);

    if (isExpired) {
        return null;
    }

    if (children) {
        return (
            <>
                <Mask
                    placement="center"
                    fullscreen={false}
                    themeOverride={{ zIndex }}
                >
                    {children}
                </Mask>
            </>
        );
    }

    return (
        <>
            <Mask
                placement="center"
                fullscreen={false}
                containFocus={false}
                themeOverride={{ zIndex }}
            >
                <Flex
                    as="div"
                    direction="column"
                    padding={padding}
                >
                    {overlayData.iconName && (
                        <Flex.Item textAlign="center">
                            <Text color={overlayData.iconColor}>
                                <Icon name={overlayData.iconName} size={iconSize} />
                            </Text>
                        </Flex.Item>
                    )}

                    {overlayData.title && (
                        <Flex.Item textAlign="center">
                            <Text variant={titleVariant}>{overlayData.title}</Text>
                            {overlayData.subtitle && (
                                <Text as="h3" variant="h3">{overlayData.subtitle}</Text>
                            )}
                        </Flex.Item>
                    )}

                    {overlayData.note && (
                        <Flex.Item>
                            <Alert
                                variant={overlayData.alertVariant}
                                title={overlayData.alertTitle || 'Note'}
                            >
                                {overlayData.note}
                            </Alert>
                        </Flex.Item>
                    )}

                    {showExpandedInfo && (courseData.title || sessionData?.exam?.title) && (
                        <Flex.Item>
                            <List isUnstyled itemSpacing={CONSTANTS.STYLES.CARD_ITEM_SPACING}>
                                {courseData.title && (
                                    <List.Item>
                                        <LabelAndText label="Course" text={courseData.title} />
                                    </List.Item>
                                )}
                                {sessionData?.exam?.title && (
                                    <List.Item>
                                        <LabelAndText
                                            label="Exam"
                                            text={sessionData?.exam?.title}
                                        />
                                    </List.Item>
                                )}
                            </List>
                        </Flex.Item>
                    )}

                    {overlayData.buttonText && (
                        <>
                            <Flex.Item textAlign="center">
                                {size === 'small' ? (
                                    <Link variant="standalone-small" onClick={handleAcknowledgement}>
                                        {overlayData.buttonText}
                                    </Link>
                                ) : (
                                    <Button
                                        size="small"
                                        margin="medium none none none"
                                        onClick={handleAcknowledgement}
                                        color="primary"
                                    >
                                        {overlayData.buttonText}
                                    </Button>
                                )}
                            </Flex.Item>
                            {ttlConstant && (
                                <Flex.Item textAlign="center" margin="small none none none">
                                    <div
                                        style={{
                                            width: '40%',
                                            minWidth: '75px',
                                            height: '3px',
                                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                            borderRadius: '2px',
                                            overflow: 'hidden',
                                            margin: '0 auto'
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: `${timeRemaining}%`,
                                                height: '100%',
                                                backgroundColor: '#0374B5',
                                                transition: 'width 0.1s linear',
                                                borderRadius: '2px'
                                            }}
                                        />
                                    </div>
                                </Flex.Item>
                            )}
                        </>
                    )}
                </Flex>
            </Mask>
        </>
    );
};

export default BaseOverlay;
